import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { trackEvent } from './lib/analytics';
import {
  Camera,
  Calendar,
  Users,
  ArrowRight,
  ChevronDown,
  ChevronUp,
  MapPin,
  Instagram,
  Mail,
  Phone,
  Star
} from 'lucide-react';

const LandingPage = () => {
  const navigate = useNavigate();
  const [openFaq, setOpenFaq] = useState(null);

  // Initial content state with defaults
  const [content, setContent] = useState({
    hero: {
      title: "Thaathayya\nIllu",
      subtitle: "A home built to create memories — rooted in Telugu culture and yesteryear.",
      image: "https://images.unsplash.com/photo-1605218427368-35b863773ba9?q=80&w=2070&auto=format&fit=crop"
    },
    about: {
      tag: "Who We Are",
      title: "Step into a home that feels like a memory.",
      description1: "Thaathayya Illu is a thoughtfully created home, built with a single intention: to hold memory, culture, and the quiet rhythms of old Telugu life.",
      description2: "Inspired by traditional homes, courtyards, and everyday rituals of the past, the space is designed to feel familiar and grounding — a place where moments unfold naturally and memories are made with ease.",
      image1: "https://images.unsplash.com/photo-1596627685698-466d6d8d6725?q=80&w=1000&auto=format&fit=crop",
      image2: "https://images.unsplash.com/photo-1629079377484-8846c430e38a?q=80&w=1000&auto=format&fit=crop"
    },
    offerings: [
      {
        title: "Photoshoots & Events",
        description: "For sessions from 1 November 2026, photoshoots start at ₹7,200. Pricing varies by shoot type and total headcount.",
        image: "https://images.unsplash.com/photo-1533227297464-675ad7a47f70?q=80&w=1000&auto=format&fit=crop",
        link: "/booking",
        linkText: "Book Now",
        ctaType: "link"
      },
      {
        title: "Celebrations",
        description: "We turn a narrative into a day. From moonlit dinners to intimate gatherings that feel like home.",
        image: "https://images.unsplash.com/photo-1511795409834-ef04bbd61622?q=80&w=1000&auto=format&fit=crop",
        link: "",
        linkText: "Contact Us",
        ctaType: "whatsapp"
      },
      {
        title: "Signature Events",
        description: "Cultural workshops, art gatherings, and community events hosted by Thaathayya Illu.",
        image: "https://images.unsplash.com/photo-1519671482502-9759101d23f7?q=80&w=1000&auto=format&fit=crop",
        link: "",
        linkText: "",
        ctaType: "none"
      }
    ],
    vision: {
      subtitle: "Our Philosophy",
      image: "https://images.unsplash.com/photo-1598928506311-c55ded91a20c?q=80&w=1000&auto=format&fit=crop",
      cards: [
        { icon: 'Calendar', title: "Rooted in Culture", description: "Rooted in Telugu cultural aesthetics and sensibilities, bringing the charm of yesteryears to life." },
        { icon: 'Star', title: "Slow Living", description: "Inspired by yesteryear homes, courtyards, and slow living. A pause from the fast-paced world." },
        { icon: 'Camera', title: "Storytelling", description: "Calm, uncluttered spaces that let people and moments take centre stage. Designed for emotion and intimacy." },
        { icon: 'Users', title: "Meaning Over Scale", description: "Ideal for those who value meaning over scale. Perfect for intimate gatherings and soulful captures." }
      ]
    },
    faqs: [
      {
        question: "How do I book a photoshoot?",
        answer: "You can book directly through our website by selecting your preferred date and duration (3, 6, 8, or 12 hours)."
      },
      {
        question: "What are the pricing options?",
        answer: "For sessions from 1 November 2026, tariffs depend on duration and package. A 3-hour couple or family shoot is ₹7,200 (up to 10 people); commercial and Movie / OTT packages are selected during booking."
      },
      {
        question: "Is there a changing room available?",
        answer: "Yes, we provide a dedicated private space for changing and makeup adjustments during your slot."
      },
      {
        question: "What is your cancellation policy?",
        answer: "Cancellations made 48 hours before the slot receive a 50% refund. Cancellations within 48 hours are non-refundable."
      },
      {
        question: "Can we bring props?",
        answer: "Yes, you are welcome to bring props. We request that you ensure they do not cause damage to the property."
      }
    ],
    instagram: {
      profile: {
        username: "thaathayya.illu",
        fullName: "Thaathayya illu",
        bio: "That palleturi illu that our father, mother and we have dreamt of. Mokila, Hyd | Family-operated",
        avatar: "https://instagram.fcok11-1.fna.fbcdn.net/v/t51.2885-19/471012170_1091218556094236_540974868953112444_n.jpg?_nc_ht=instagram.fcok11-1.fna&_nc_cat=109&_nc_ohc=f6Jq_KxL7c8Q7kNvwF_Q4R-&_nc_gid=5319qIRRA7ctdiBLnq7kNQ&edm=AL09I-kBAAAA&ccb=7-5&oh=00_Afv5t_H_Y5zGZB5b9H7_yB-Gz-D_V-J_H_Y5zGZB5b9H7_y&oe=698E64E4&_nc_sid=58cdad",
        stats: {
          posts: 183,
          followers: "12.6K",
          following: 2
        }
      },
      posts: [
        {
          url: "https://www.instagram.com/thaathayya.illu/reel/DUU9bmuk89v/",
          // Instagram's own CDN URLs are signed and expire — re-hosted permanently
          // in our own GCS bucket instead of hotlinking Instagram's ephemeral link.
          thumbnail: "https://storage.googleapis.com/thaathayya-website-images/instagram/post1.jpg",
          type: "video"
        },
        {
          url: "https://www.instagram.com/thaathayya.illu/reel/DUFeHTqCRqu/",
          thumbnail: "https://storage.googleapis.com/thaathayya-website-images/instagram/post2.jpg",
          type: "video"
        },
        {
          url: "https://www.instagram.com/thaathayya.illu/reel/DUA7COrEhOs/",
          thumbnail: "https://storage.googleapis.com/thaathayya-website-images/instagram/post3.jpg",
          type: "video"
        },
        {
          url: "https://www.instagram.com/thaathayya.illu/reel/DT4dgMqiZRK/",
          thumbnail: "https://storage.googleapis.com/thaathayya-website-images/instagram/post4.jpg",
          type: "video"
        }
      ]
    },
    location: {
      title: "Find Your Path Home",
      address: "Mokila, Hyderabad, Telangana",
      fullAddress: "Thaathayya Illu, Mokila, Shankarpalli Road, Hyderabad - 501203",
      mapEmbedUrl: "https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3806.8973642559663!2d78.1903281!3d17.4167125!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3bcbef0004ca7741%3A0xe04df57a5baf5d67!2sThaathayya%20Illu!5e0!3m2!1sen!2sin!4v1770562582719!5m2!1sen!2sin",
      directionsUrl: "https://maps.app.goo.gl/v6Xm7Tb32v82ePUU6",
      phone: "+91 93982 35164",
      email: "thaathayyaillu@gmail.com",
      whatsappNumber: "919398235164"
    }
  });

  // Fetch dynamic content from public endpoint
  useEffect(() => {
    const fetchContent = async () => {
      try {
        const response = await axios.get('/api/admin/settings/public?id=landing_page');
        console.log('Fetched landing page data:', response.data);

        if (response.data.success && response.data.settings && Object.keys(response.data.settings).length > 0) {
          const fetchedSettings = response.data.settings;
          console.log('Applying settings:', fetchedSettings);

          // Deep merge: Replace arrays entirely from fetched data, merge objects
          setContent(prev => {
            const merged = { ...prev };

            Object.keys(fetchedSettings).forEach(key => {
              const fetchedValue = fetchedSettings[key];
              if (fetchedValue === null || fetchedValue === undefined) return;

              if (Array.isArray(fetchedValue)) {
                // Replace and migrate arrays
                if (fetchedValue.length > 0) {
                  if (key === 'offerings') {
                    // Migrate offerings CTAs
                    merged.offerings = fetchedValue.map(offering => {
                      const title = offering.title || '';
                      let ctaType = offering.ctaType;
                      let linkText = offering.linkText;
                      let link = offering.link;

                      // Migration logic for specific sections
                      if (title.toLowerCase().includes('celebration')) {
                        ctaType = 'whatsapp';
                        linkText = 'Contact Us';
                        link = '';
                      } else if (title.toLowerCase().includes('signature')) {
                        ctaType = 'none';
                        linkText = '';
                        link = '';
                      }

                      return { ...offering, ctaType, linkText, link };
                    });
                  } else {
                    merged[key] = fetchedValue;
                  }
                }
              } else if (typeof fetchedValue === 'object') {
                // Merge object fields (hero, about, vision)
                merged[key] = { ...prev[key] };
                Object.keys(fetchedValue).forEach(subKey => {
                  const subValue = fetchedValue[subKey];

                  // Special handling for vision.cards migration
                  if (key === 'vision' && subKey === 'cards' && Array.isArray(subValue)) {
                    merged.vision.cards = subValue.map(card => ({
                      icon: card.icon || 'Star',
                      title: card.title || '',
                      description: card.description || card.text || ''
                    }));
                  } else if (key === 'instagram' && subKey === 'reels' && Array.isArray(subValue)) {
                    // Migrate reels to posts if legacy data exists
                    merged.instagram.posts = subValue.map(reel => ({
                      url: reel.url || '',
                      thumbnail: reel.thumbnail || '',
                      type: 'video'
                    }));
                  } else if (subValue !== null && subValue !== undefined && subValue !== '') {
                    merged[key][subKey] = subValue;
                  }
                });
              } else if (fetchedValue !== '') {
                // Replace primitives
                merged[key] = fetchedValue;
              }
            });

            console.log('Merged content:', merged);
            return merged;
          });
        }
      } catch (error) {
        console.error('Failed to fetch landing page content', error);
      }
    };
    fetchContent();
  }, []);

  const toggleFaq = (index) => {
    setOpenFaq(openFaq === index ? null : index);
  };

  // Helper to get icon component by name
  const getIcon = (name) => {
    const icons = { Camera, Calendar, Users, Star };
    const IconComponent = icons[name] || Star;
    return <IconComponent className="w-5 h-5" />;
  };

  const handleBookNow = (e, source = 'unknown') => {
    if (e) e.stopPropagation();
    trackEvent('select_content', { content_type: 'cta', item_id: 'book_now', source });
    navigate('/booking');
  };

  const WHATSAPP_NUMBER = content.location?.whatsappNumber || "919398235164";
  const whatsappMessage = encodeURIComponent("Hi, I'm interested in booking at Thaathayya Illu.");
  const whatsappUrl = `https://wa.me/${WHATSAPP_NUMBER}?text=${whatsappMessage}`;

  return (
    <div className="min-h-screen bg-[#F4ECDE] font-sans text-[#471F21]">
      {/* Navigation */}
      <nav className="sticky top-0 w-full bg-[#F4ECDE]/90 backdrop-blur-sm z-50 border-b border-[#471F21]/10 -mb-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            <div className="flex-shrink-0 flex items-center">
              <img src="/logo-dark.svg" alt="Thaathayya Illu" className="h-12 w-auto" />
              <span className="ml-3 text-xl font-bold tracking-widest hidden md:block">THAATHAYYA ILLU</span>
            </div>
            <div className="hidden md:flex space-x-8 items-center">
              <a href="#about" className="hover:text-[#8B4446] transition-colors">Our Story</a>
              <a href="#offerings" className="hover:text-[#8B4446] transition-colors">Offerings</a>
              <a href="#faq" className="hover:text-[#8B4446] transition-colors">FAQ</a>
              <button
                onClick={(e) => handleBookNow(e, 'nav_bar')}
                className="bg-[#471F21] text-white px-6 py-2 rounded-full hover:bg-[#8B4446] transition-colors"
              >
                Book Now
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <div className="relative h-screen">
        <div className="absolute inset-0 z-0">
          <img
            src={content.hero.image}
            alt="Hero"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-black/30"></div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-full flex flex-col justify-center text-white pt-20">
          <h1 className="text-5xl md:text-7xl font-serif mb-6 leading-tight whitespace-pre-line">
            {content.hero.title}
          </h1>
          <p className="text-xl md:text-2xl max-w-2xl font-light mb-8 leading-relaxed">
            {content.hero.subtitle}
          </p>
          <div className="flex flex-col sm:flex-row gap-4">
            <button
              onClick={(e) => handleBookNow(e, 'hero')}
              className="bg-[#F4ECDE] text-[#471F21] px-8 py-4 rounded-full text-lg font-semibold hover:bg-white transition-all flex items-center justify-center"
            >
              Book your photoshoot <ArrowRight className="ml-2 w-5 h-5" />
            </button>
            <button
              onClick={() => document.getElementById('about').scrollIntoView({ behavior: 'smooth' })}
              className="border-2 border-[#F4ECDE] text-[#F4ECDE] px-8 py-4 rounded-full text-lg font-semibold hover:bg-[#F4ECDE]/10 transition-all"
            >
              Explore the Space
            </button>
          </div>
        </div>
      </div>

      {/* About Section */}
      <section id="about" className="py-20 px-4 bg-[#F4ECDE]">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-block px-4 py-1 border border-[#471F21] rounded-full text-sm mb-6">
                {content.about.tag}
              </div>
              <h2 className="text-4xl md:text-5xl font-serif mb-8 leading-tight">
                {content.about.title}
              </h2>
              <p className="text-lg text-[#471F21]/80 mb-6 leading-relaxed">
                {content.about.description1}
              </p>
              <p className="text-lg text-[#471F21]/80 mb-6 leading-relaxed">
                {content.about.description2}
              </p>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <img
                src={content.about.image1}
                alt="About 1"
                className="rounded-2xl w-full h-64 object-cover transform translate-y-8"
              />
              <img
                src={content.about.image2}
                alt="About 2"
                className="rounded-2xl w-full h-64 object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Offerings Section */}
      <section id="offerings" className="py-20 px-4 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-serif mb-4">Our Offerings</h2>
            <p className="text-xl text-gray-600">Curated experiences for your special moments</p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {content.offerings.map((offering, index) => (
              <div key={index} className="group cursor-pointer" onClick={(e) => {
                trackEvent('select_item', { item_list_name: 'offerings', item_id: offering.title, index });
                if (offering.ctaType === 'whatsapp') {
                  handleBookNow(e, 'offering_card');
                } else if (offering.ctaType === 'link' && offering.link) {
                  navigate(offering.link);
                }
              }}>
                <div className="relative overflow-hidden rounded-2xl mb-6">
                  <img
                    src={offering.image}
                    alt={offering.title}
                    className="w-full h-80 object-cover transform group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute top-4 right-4 bg-white/90 px-3 py-1 rounded-full text-xs font-semibold">
                    0{index + 1}
                  </div>
                </div>
                <h3 className="text-2xl font-serif mb-2">{offering.title}</h3>
                <p className="text-gray-600 mb-4">
                  {offering.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Spirit of the Space */}
      <section className="py-20 px-4 bg-[#F4ECDE]">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <div className="inline-block px-4 py-1 border border-[#471F21] rounded-full text-sm mb-6">
              Our Philosophy
            </div>
            <h2 className="text-4xl font-serif mb-4">{content.vision.title || "The Spirit of the Space"}</h2>
          </div>

          <div className="grid md:grid-cols-2 gap-8 items-center">
            <div className="order-2 md:order-1 relative">
              <img
                src={content.vision.image}
                alt="Spirit of the Space"
                className="rounded-2xl w-full h-[500px] object-cover shadow-xl"
              />
              <div className="absolute -bottom-6 -right-6 bg-white p-6 rounded-xl shadow-lg max-w-xs hidden md:block">
                <p className="text-[#471F21] font-serif text-lg">
                  "Designed for emotion, intimacy, and storytelling."
                </p>
              </div>
            </div>

            <div className="order-1 md:order-2 space-y-8">
              {content.vision.cards.map((card, index) => (
                <div key={index} className="flex items-start">
                  <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center mr-6 text-[#471F21] shadow-sm flex-shrink-0">
                    {getIcon(card.icon)}
                  </div>
                  <div>
                    <h3 className="text-xl font-bold mb-2">{card.title}</h3>
                    <p className="text-gray-600 leading-relaxed">{card.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials (Static for now) */}
      <section className="py-20 px-4 bg-white">
        <div className="max-w-7xl mx-auto text-center">
          <div className="inline-block px-4 py-1 border border-[#471F21] rounded-full text-sm mb-6">
            What Our Clients Say
          </div>
          <h2 className="text-4xl font-serif mb-12">
            What our guests felt, in their own words.
          </h2>

          <div className="grid md:grid-cols-3 gap-8">
            {(content.testimonials || []).map((testimonial, index) => (
              <div key={index} className="bg-[#F9F6F0] p-8 rounded-2xl text-left">
                <div className="flex text-[#8B4446] mb-4">
                  {[...Array(5)].map((_, i) => <Star key={i} className="w-4 h-4 fill-current" />)}
                </div>
                <p className="text-gray-700 mb-6 italic">
                  "{testimonial.quote}"
                </p>
                <div className="flex items-center">
                  {testimonial.image ? (
                    <img src={testimonial.image} alt={testimonial.name} className="w-10 h-10 rounded-full mr-3 object-cover" />
                  ) : (
                    <div className="w-10 h-10 bg-gray-300 rounded-full mr-3"></div>
                  )}
                  <div>
                    <div className="font-semibold">{testimonial.name}</div>
                    <div className="text-xs text-gray-500">{testimonial.role}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section id="faq" className="py-20 px-4 bg-[#F4ECDE]">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-serif mb-4">Frequently Asked Questions</h2>
            <p className="text-gray-600">Everything you need to know about booking with us</p>
          </div>

          <div className="space-y-4">
            {content.faqs && content.faqs.map((faq, index) => (
              <div
                key={index}
                className="bg-white rounded-xl overflow-hidden transition-all duration-300 shadow-sm hover:shadow-md"
              >
                <button
                  className="w-full px-6 py-4 text-left flex justify-between items-center focus:outline-none"
                  onClick={() => toggleFaq(index)}
                >
                  <span className="font-medium text-lg">{faq.question}</span>
                  {openFaq === index ? (
                    <ChevronUp className="w-5 h-5 text-[#8B4446]" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-gray-400" />
                  )}
                </button>
                <div
                  className={`px-6 overflow-hidden transition-all duration-300 ${openFaq === index ? 'max-h-40 py-4 border-t border-gray-100' : 'max-h-0'
                    }`}
                >
                  <p className="text-gray-600">{faq.answer}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Instagram Profile Showcase */}
      <section className="bg-[#471F21] py-24 px-4 overflow-hidden">
        <div className="max-w-4xl mx-auto bg-white rounded-3xl overflow-hidden shadow-2xl relative">
          <div className="p-8 md:p-12">
            {/* Profile Header */}
            <div className="flex flex-col md:flex-row items-center md:items-start gap-8 mb-12">
              <div className="relative group">
                <div className="w-32 h-32 md:w-40 md:h-40 rounded-full p-1 bg-gradient-to-tr from-[#f9ce34] via-[#ee2a7b] to-[#6228d7]">
                  <img
                    src={content.instagram?.profile?.avatar || "/avatar-fallback.jpg"}
                    alt="Profile"
                    className="w-full h-full rounded-full object-cover border-4 border-white"
                    onError={(e) => {
                      e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(content.instagram?.profile?.username || 'TI')}&background=471F21&color=F4ECDE`;
                    }}
                  />
                </div>
                <div className="absolute -bottom-2 right-4 bg-blue-500 text-white p-1.5 rounded-full border-2 border-white shadow-lg">
                  <Star className="w-4 h-4 fill-current" />
                </div>
              </div>

              <div className="flex-1 text-center md:text-left">
                <div className="flex flex-col md:flex-row items-center gap-4 mb-6">
                  <h2 className="text-2xl font-bold text-gray-900 leading-none">
                    {content.instagram?.profile?.username || 'thaathayya.illu'}
                  </h2>
                  <div className="flex gap-2">
                    <button
                      onClick={() => window.open('https://www.instagram.com/thaathayya.illu/', '_blank')}
                      className="bg-[#0095f6] hover:bg-[#1877f2] text-white px-6 py-1.5 rounded-lg text-sm font-semibold transition-colors"
                    >
                      Follow
                    </button>
                    <button
                      onClick={() => window.open('https://www.instagram.com/thaathayya.illu/', '_blank')}
                      className="bg-gray-100 hover:bg-gray-200 text-gray-900 px-4 py-1.5 rounded-lg text-sm font-semibold transition-colors"
                    >
                      Message
                    </button>
                  </div>
                </div>

                <div className="flex justify-center md:justify-start gap-8 mb-6 text-sm">
                  <div><span className="font-bold">{content.instagram?.profile?.stats?.posts || 183}</span> posts</div>
                  <div><span className="font-bold">{content.instagram?.profile?.stats?.followers || "12.6K"}</span> followers</div>
                  <div><span className="font-bold">{content.instagram?.profile?.stats?.following || 2}</span> following</div>
                </div>

                <div>
                  <h3 className="font-bold text-gray-900 mb-1">{content.instagram?.profile?.fullName || "Thaathayya illu"}</h3>
                  <p className="text-gray-600 text-sm whitespace-pre-line leading-relaxed max-w-md">
                    {content.instagram?.profile?.bio || "That palleturi illu that our father, mother and we have dreamt of."}
                  </p>
                </div>
              </div>
            </div>

            {/* Grid Posts */}
            <div className="grid grid-cols-3 gap-1 md:gap-4">
              {(content.instagram?.posts || []).map((post, index) => (
                <a
                  key={index}
                  href={post.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="relative aspect-square group overflow-hidden bg-gray-100"
                >
                  <img
                    src={post.thumbnail}
                    alt={`Instagram Post ${index + 1}`}
                    className="w-full h-full object-cover transition-opacity duration-300 group-hover:opacity-80"
                    onError={(e) => { e.target.style.display = 'none'; }}
                  />
                  {/* Hover Overlay */}
                  <div className="absolute inset-0 bg-black/20 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-4 text-white">
                    <div className="flex items-center gap-1">
                      <Instagram className="w-5 h-5 fill-current" />
                      <span className="font-bold text-sm">View</span>
                    </div>
                  </div>
                  {post.type === 'video' && (
                    <div className="absolute top-2 right-2 text-white drop-shadow-md">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" clipRule="evenodd" />
                      </svg>
                    </div>
                  )}
                </a>
              ))}
            </div>
          </div>

          {/* Footer Call to Action */}
          <div className="bg-gray-50 border-t border-gray-100 p-6 text-center">
            <button
              onClick={() => window.open('https://www.instagram.com/thaathayya.illu/', '_blank')}
              className="text-[#0095f6] font-semibold text-sm hover:underline"
            >
              Explore more on Instagram
            </button>
          </div>
        </div>
      </section>

      {/* Location Section */}
      <section id="location" className="bg-[#F4ECDE] py-24 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#471F21]/5 text-[#471F21] text-sm font-medium mb-6">
                <MapPin className="w-4 h-4" />
                Location
              </div>
              <h2 className="text-4xl md:text-5xl font-serif text-[#471F21] mb-8 leading-tight">
                {content.location?.title || "Find Your Path Home"}
              </h2>

              <div className="space-y-8 mb-12">
                <div className="flex gap-4">
                  <div className="w-12 h-12 rounded-2xl bg-[#471F21] flex items-center justify-center shrink-0">
                    <MapPin className="w-6 h-6 text-[#F4ECDE]" />
                  </div>
                  <div>
                    <h4 className="font-bold text-[#471F21] mb-1 text-lg">Address</h4>
                    <p className="text-[#471F21]/70 leading-relaxed">
                      {content.location?.fullAddress || "Thaathayya Illu, Mokila, Shankarpalli Road, Hyderabad - 501203"}
                    </p>
                  </div>
                </div>

                <div className="flex gap-4">
                  <div className="w-12 h-12 rounded-2xl bg-[#471F21] flex items-center justify-center shrink-0">
                    <Phone className="w-6 h-6 text-[#F4ECDE]" />
                  </div>
                  <div>
                    <h4 className="font-bold text-[#471F21] mb-1 text-lg">Contact</h4>
                    <p className="text-[#471F21]/70 leading-relaxed">{content.location?.phone || "+91 93982 35164"}</p>
                  </div>
                </div>
              </div>

              <button
                onClick={() => window.open(content.location?.directionsUrl || "https://maps.app.goo.gl/v6Xm7Tb32v82ePUU6", "_blank")}
                className="group inline-flex items-center gap-3 px-8 py-4 rounded-full bg-[#471F21] text-[#F4ECDE] hover:bg-[#8B4446] transition-all font-semibold shadow-lg hover:shadow-xl"
              >
                Get Directions
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </button>
            </div>

            <div className="relative aspect-[4/3] lg:aspect-square rounded-3xl overflow-hidden shadow-2xl border-8 border-white bg-white">
              <iframe
                src={content.location?.mapEmbedUrl || "https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3806.8973642559663!2d78.1903281!3d17.4167125!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3bcbef0004ca7741%3A0xe04df57a5baf5d67!2sThaathayya%20Illu!5e0!3m2!1sen!2sin!4v1770562582719!5m2!1sen!2sin"}
                className="absolute inset-0 w-full h-full border-0 grayscale-[0.2] hover:grayscale-0 transition-all duration-700"
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Google Maps Location"
              ></iframe>
            </div>
          </div>
        </div>
      </section>
      <footer className="bg-[#471F21] text-[#F4ECDE] pt-20 pb-10 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-4 gap-12 mb-16">
            <div className="col-span-1 md:col-span-1">
              <img src="/logo-light.svg" alt="Logo" className="h-16 w-auto mb-6 opacity-90" />
              <p className="text-white/70 mb-6">
                A home built to create memories — rooted in Telugu culture and yesteryear.
              </p>
              <div className="flex space-x-4">
                <a
                  href="https://www.instagram.com/thaathayya.illu/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-white/70 hover:text-white"
                  aria-label="Follow us on Instagram"
                >
                  <Instagram className="w-5 h-5" />
                </a>
                <a
                  href={`mailto:${content.location?.email || "thaathayyaillu@gmail.com"}`}
                  className="text-white/70 hover:text-white"
                  aria-label="Email us"
                >
                  <Mail className="w-5 h-5" />
                </a>
              </div>
            </div>

            <div>
              <h4 className="text-lg font-semibold mb-6">Quick Links</h4>
              <ul className="space-y-4 text-white/70">
                <li><a href="#about" className="hover:text-white transition-colors">About Us</a></li>
                <li><a href="#offerings" className="hover:text-white transition-colors">Photoshoots</a></li>
                <li><a href="#offerings" className="hover:text-white transition-colors">Events</a></li>
              </ul>
            </div>

            <div>
              <h4 className="text-lg font-semibold mb-6">Legal</h4>
              <ul className="space-y-4 text-white/70">
                <li><a href="/terms" className="hover:text-white transition-colors">Terms & Conditions</a></li>
                <li><a href="/privacy" className="hover:text-white transition-colors">Privacy Policy</a></li>
                <li><a href="/cancellation" className="hover:text-white transition-colors">Cancellation Policy</a></li>
                <li><a href="#faq" className="hover:text-white transition-colors">FAQ</a></li>
              </ul>
            </div>

            <div>
              <h4 className="text-lg font-semibold mb-6">Visit Us</h4>
              <div className="space-y-4 text-white/70">
                <p className="flex items-start">
                  <MapPin className="w-5 h-5 mr-3 mt-1 flex-shrink-0" />
                  <span>Thaathayya Illu, Near Hyderabad,<br />Telangana, 500001</span>
                </p>
                <p className="flex items-center">
                  <Phone className="w-5 h-5 mr-3 flex-shrink-0" />
                  <span>{content.location?.phone || "+91 93982 35164"}</span>
                </p>
                <p className="flex items-center">
                  <Mail className="w-5 h-5 mr-3 flex-shrink-0" />
                  <span>{content.location?.email || "thaathayyaillu@gmail.com"}</span>
                </p>
              </div>
            </div>
          </div>

          <div className="border-t border-white/10 pt-8 text-center text-white/50 text-sm">
            © {new Date().getFullYear()} Thaathayya Illu. All rights reserved.
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
